import { esc } from "./dom.js";
import { formatRelativeTime } from "./fmt.js";

export let notificationState = {
  items: [],
  unreadCount: 0,
  panelOpen: false,
  pollTimer: null,
};

export async function fetchUnreadCount() {
  try {
    const res = await fetch("/api/notifications/unread-count", { credentials: "include" });
    if (!res.ok) return;
    const data = await res.json();
    notificationState.unreadCount = data.unread_count || 0;
    renderNotificationBell();
  } catch {}
}

export async function fetchNotifications(limit = 30) {
  try {
    const res = await fetch(`/api/notifications?limit=${limit}`, { credentials: "include" });
    if (!res.ok) return;
    const data = await res.json();
    notificationState.items = data.notifications || [];
    renderNotificationPanel();
  } catch {}
}

export function startNotificationPolling() {
  stopNotificationPolling();
  fetchUnreadCount();
  notificationState.pollTimer = window.setInterval(fetchUnreadCount, 30000);
}

export function stopNotificationPolling() {
  if (notificationState.pollTimer) {
    clearInterval(notificationState.pollTimer);
    notificationState.pollTimer = null;
  }
}

export function renderNotificationBell() {
  const bell = document.getElementById("notif-bell");
  if (!bell) return;
  const count = notificationState.unreadCount;
  bell.querySelector(".notif-bell-count").textContent = count;
  bell.querySelector(".notif-bell-count").hidden = count === 0;
  bell.querySelector(".notif-bell-dot").hidden = count <= 0;
}

export function initNotificationBell() {
  let bell = document.getElementById("notif-bell");
  if (bell) return;
  const container = document.querySelector(".header-actions") || document.querySelector(".sidebar-user");
  if (!container) return;
  bell = document.createElement("div");
  bell.id = "notif-bell";
  bell.className = "notif-bell";
  bell.innerHTML = `
    <button type="button" class="notif-bell-btn" title="通知">
      🔔
      <span class="notif-bell-dot" hidden></span>
      <span class="notif-bell-count" hidden>0</span>
    </button>
  `;
  container.prepend(bell);
  bell.querySelector(".notif-bell-btn").addEventListener("click", async (e) => {
    e.stopPropagation();
    if (notificationState.panelOpen) {
      closeNotificationPanel();
    } else {
      await openNotificationPanel();
    }
  });
  document.addEventListener("click", (e) => {
    if (notificationState.panelOpen && !e.target.closest(".notif-panel") && !e.target.closest(".notif-bell")) {
      closeNotificationPanel();
    }
  });
}

export async function openNotificationPanel() {
  notificationState.panelOpen = true;
  await fetchNotifications();
}

export function closeNotificationPanel() {
  notificationState.panelOpen = false;
  const panel = document.getElementById("notif-panel");
  if (panel) panel.remove();
}

export function renderNotificationPanel() {
  closeNotificationPanel();
  const panel = document.createElement("div");
  panel.id = "notif-panel";
  panel.className = "notif-panel";
  const items = notificationState.items;
  panel.innerHTML = `
    <div class="notif-panel-head">
      <strong>通知</strong>
      <div class="notif-panel-actions">
        ${items.length ? `<button type="button" class="notif-mark-all" data-action="notif-read-all">全部已读</button>` : ""}
        <button type="button" class="notif-close" data-action="notif-close">×</button>
      </div>
    </div>
    <div class="notif-panel-body">
      ${items.length
        ? items.map(renderNotificationItem).join("")
        : `<p class="muted-empty">暂无通知</p>`}
    </div>
  `;
  document.body.appendChild(panel);
  panel.querySelector("[data-action='notif-close']").addEventListener("click", closeNotificationPanel);
  panel.querySelector("[data-action='notif-read-all']")?.addEventListener("click", markAllRead);
  panel.querySelectorAll("[data-action='notif-read']").forEach((btn) => {
    btn.addEventListener("click", () => markAsRead(btn.dataset.notifId));
  });
  positionPanel(panel);
}

function positionPanel(panel) {
  const bell = document.getElementById("notif-bell");
  if (!bell) {
    panel.style.top = "16px";
    panel.style.right = "16px";
    return;
  }
  const rect = bell.getBoundingClientRect();
  // 侧栏铃铛 (左侧) 时面板在右侧弹出, 锚定 bell 底部向上展开;
  // 头部铃铛 (右上) 时面板在下方弹出, 右对齐.
  const inSidebar = rect.left < window.innerWidth / 2;
  if (inSidebar) {
    const left = rect.right + 8;
    panel.style.left = `${Math.min(left, window.innerWidth - 16 - 360)}px`;
    panel.style.bottom = `${Math.max(8, window.innerHeight - rect.bottom)}px`;
    panel.style.top = "auto";
    panel.style.right = "auto";
    // panel 从 bell 底部向上展开; max-height 必须限制在 bell 上方的可用空间内,
    // 否则通知数量多时顶部会冲出视窗, head/关闭按钮被推到屏幕外无法点击.
    panel.style.maxHeight = `${Math.max(240, rect.bottom - 16)}px`;
  } else {
    panel.style.top = `${rect.bottom + 8}px`;
    panel.style.right = `${Math.max(8, window.innerWidth - rect.right)}px`;
    panel.style.left = "auto";
    panel.style.bottom = "auto";
  }
}

function renderNotificationItem(n) {
  const cls = `notif-item${n.is_read ? " notif-read" : ""}`;
  const sourceLabel = { watch: "盯盘", strategy: "策略", system: "系统", training: "训练" }[n.source] || n.source;
  const time = n.created_at ? formatRelativeTime(n.created_at) : "";
  const details = Array.isArray(n.data?.signal_details) ? n.data.signal_details : [];
  const detailsHtml = details.length
    ? `<details class="notif-signal-details">
        <summary>查看 ${details.length} 个触发信号</summary>
        <ul>
          ${details
            .map(
              (d) => `<li><strong>${esc(d.label || d.key || "—")}</strong>${d.reason ? ` <span class="muted">${esc(d.reason)}</span>` : ""}</li>`,
            )
            .join("")}
        </ul>
      </details>`
    : "";
  const suggestion = n.data?.advice_suggestion
    ? `<div class="notif-item-suggestion">建议：${esc(n.data.advice_suggestion)}</div>`
    : "";
  return `
    <div class="${cls}" data-notif-id="${esc(n.id)}">
      <div class="notif-item-head">
        <span class="notif-source notif-source-${esc(n.source)}">${esc(sourceLabel)}</span>
        ${!n.is_read ? `<span class="notif-unread-dot"></span>` : ""}
        ${time ? `<span class="notif-time">${esc(time)}</span>` : ""}
      </div>
      <div class="notif-item-title">${esc(n.title)}</div>
      ${n.body ? `<div class="notif-item-body">${esc(n.body)}</div>` : ""}
      ${suggestion}
      ${detailsHtml}
      <div class="notif-item-actions">
        ${!n.is_read ? `<button type="button" class="notif-mark-btn" data-action="notif-read" data-notif-id="${esc(n.id)}">标记已读</button>` : ""}
      </div>
    </div>
  `;
}

async function markAsRead(id) {
  try {
    await fetch(`/api/notifications/${encodeURIComponent(id)}/read`, { method: "PATCH", credentials: "include" });
    const item = notificationState.items.find((n) => n.id === id);
    if (item) item.is_read = 1;
    notificationState.unreadCount = Math.max(0, notificationState.unreadCount - 1);
    renderNotificationBell();
    renderNotificationPanel();
  } catch {}
}

async function markAllRead() {
  try {
    await fetch("/api/notifications/read-all", { method: "POST", credentials: "include" });
    for (const item of notificationState.items) item.is_read = 1;
    notificationState.unreadCount = 0;
    renderNotificationBell();
    renderNotificationPanel();
  } catch {}
}
